import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import { brandColors } from './colors';

declare module '@mui/material/styles' {
  interface Palette {
    custom: {
      surface: string;
      lilac: string;
      gradient: string;
    };
  }
  interface PaletteOptions {
    custom?: {
      surface: string;
      lilac: string;
      gradient: string;
    };
  }
}

const baseTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: brandColors.purple,
      dark: brandColors.purpleDeep,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: brandColors.pink,
      contrastText: '#FFFFFF',
    },
    warning: {
      main: brandColors.yellow,
      contrastText: brandColors.charcoal,
    },
    error: {
      main: brandColors.accent.red,
    },
    success: {
      main: brandColors.accent.green,
    },
    background: {
      default: brandColors.bg,
      paper: brandColors.paper,
    },
    text: {
      primary: brandColors.charcoal,
      secondary: brandColors.gray,
    },
    divider: 'rgba(176, 73, 182, 0.18)',
    custom: {
      surface: brandColors.surface,
      lilac: brandColors.lilac,
      gradient: brandColors.gradient,
    },
  },
  shape: {
    borderRadius: 12,
  },
  typography: {
    fontFamily: [
      'Inter',
      'system-ui',
      '-apple-system',
      'Segoe UI',
      'Roboto',
      'Helvetica',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: '1px solid rgba(176, 73, 182, 0.14)',
          boxShadow: '0 10px 30px rgba(68, 78, 84, 0.08)',
          borderRadius: 12,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '10px 24px',
        },
        containedPrimary: {
          boxShadow: '0 10px 20px rgba(176, 73, 182, 0.25)',
          '&:hover': {
            boxShadow: '0 12px 24px rgba(176, 73, 182, 0.35)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'medium',
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(68, 78, 84, 0.1)',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: brandColors.gray,
            borderRadius: '4px',
          },
        },
      },
    },
  },
});

export const theme = responsiveFontSizes(baseTheme);
